import { useState, useEffect, useMemo } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { Plus, Users, Eye, Pencil, Trash2, BookOpen, Heart, Copy, MoreHorizontal, Loader2, AlertTriangle, KeyRound } from 'lucide-react'
import { EditTableDialog } from './EditTableDialog'
import { JoinCampaignDialog } from './JoinCampaignDialog'
import { toast } from 'sonner'
import { useTables, useAPIHealth } from '@/hooks/useBackend'
import { canManageCampaign, canCreateCampaign } from '@/utils/permissions'
import { debugLog } from '@/utils/debug'
import { getMediaUrl } from '@/services/api'
import { cn } from '@/lib/utils'
import type { User, Table } from '@/types'

interface DashboardProps {
  user: User
  onNavigateToTable: (tableId: string) => void
}

type CampaignFilter = 'todas' | 'ativas' | 'arquivadas'

export function Dashboard({ user, onNavigateToTable }: DashboardProps) {
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showJoinDialog, setShowJoinDialog] = useState(false)
  const [selectedTable, setSelectedTable] = useState<Table | null>(null)
  const [filter, setFilter] = useState<CampaignFilter>('todas')

  const {
    tables,
    isLoading,
    loadTables,
    updateTable,
    deleteTable,
    duplicateTable,
    updateTableImage
  } = useTables()

  const { isHealthy, checkHealth } = useAPIHealth()

  // Carregar mesas do usuário e verificar estado da API
  useEffect(() => {
    checkHealth()
    loadTables().catch(error => {
      console.error('Erro ao carregar campanhas:', error)
      toast.error('Erro ao carregar campanhas. Verifique se o backend está rodando.')
    })

    // Recarregar quando a página ganhar foco
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadTables().catch(console.error)
      }
    }

    const handleFocus = () => {
      loadTables().catch(console.error)
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleFocus)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleFocus)
    }
  }, [loadTables, checkHealth, user])

  const filteredTables = useMemo(() => {
    if (filter === 'ativas') return tables.filter(t => t.is_active)
    if (filter === 'arquivadas') return tables.filter(t => !t.is_active)
    return tables
  }, [tables, filter])

  // Criação de campanha migrou para a rota /campanhas/nova (2026-07-22,
  // docs/PLANO_CRIACAO_CAMPANHA.md): tela própria, mobile-first.
  const goToCreateCampaign = () => {
    window.location.href = '/campanhas/nova'
  }

  const handleEditTable = async (updates: Partial<Table>, imageFile?: File) => {
    if (!selectedTable) return

    try {
      // Primeiro, atualizar os dados da campanha
      await updateTable(selectedTable.id, {
        name: updates.name,
        description: updates.description,
        subject: updates.subject,
        image_url: updates.image_url
      })

      // Se tiver uma nova imagem, fazer upload separado
      if (imageFile) {
        const formData = new FormData()
        formData.append('image', imageFile)
        formData.append('table_id', selectedTable.id)
        await updateTableImage(selectedTable.id, formData)
      }

      setShowEditDialog(false)
      setSelectedTable(null)
    } catch (error) {
      // Erro já tratado no hook
    }
  }

  const handleDeleteTable = async () => {
    if (!selectedTable) return

    try {
      await deleteTable(selectedTable.id)
      setShowDeleteDialog(false)
      setSelectedTable(null)
    } catch (error) {
      // Erro já tratado no hook
    }
  }

  const handleDuplicateTable = async (tableId: string) => {
    try {
      await duplicateTable(tableId)
    } catch (error) {
      // Erro já tratado no hook
    }
  }

  // Capa real da mesa, se houver; sem capa o card usa o painel cinema-dark com glow
  const getTableImage = (table: Table): string | null => {
    const imageUrl = table.imageUrl || table.image_url
    return imageUrl ? getMediaUrl(imageUrl) : null
  }

  const handleTableNavigation = (tableId: string) => {
    debugLog('NAVEGAÇÃO', `Tentando navegar para a mesa com ID: ${tableId}`)

    if (typeof onNavigateToTable !== 'function') {
      console.error('ERRO CRÍTICO: onNavigateToTable não é uma função!', onNavigateToTable)
      return
    }

    try {
      onNavigateToTable(tableId)
    } catch (error) {
      console.error('ERRO na navegação:', error)
    }
  }

  // Verificar se backend está rodando
  if (isHealthy === false) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-4">
        <Card className="mx-auto max-w-md overflow-hidden rounded-xl">
          <CardHeader className="pt-8 text-center">
            <AlertTriangle className="mx-auto mb-2 h-12 w-12 text-error" />
            <CardTitle className="text-2xl font-black uppercase italic">Servidor indisponível</CardTitle>
            <CardDescription>
              Não foi possível conectar ao servidor. Aguarde alguns instantes e tente novamente.
            </CardDescription>
          </CardHeader>
          <CardContent className="px-6 pb-8">
            <Button
              onClick={() => window.location.reload()}
              className="w-full font-bold uppercase tracking-wide"
            >
              Verificar novamente
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500 sm:space-y-8">
      {/* Saudação */}
      <div className="border-b border-border pb-4 sm:pb-6">
        <h1 className="text-2xl font-black uppercase italic leading-tight tracking-[-0.035em] sm:text-3xl">
          Bem-vindo(a), <span className="text-primary">{user.full_name}</span>
        </h1>
        <p className="mt-1 hidden text-sm font-medium text-muted-foreground sm:block sm:text-base">
          {user.role === 'superadmin'
            ? 'Painel completo — gerencie usuários, campanhas e o sistema'
            : user.role === 'professor'
            ? 'Gerencie suas campanhas educacionais e acompanhe o progresso dos estudantes'
            : 'Participe de aventuras educacionais e desenvolva suas habilidades'
          }
        </p>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-xl font-black uppercase italic tracking-tighter sm:text-2xl">Suas campanhas</h2>
        <div className="flex w-full gap-2 sm:w-auto">
          {canCreateCampaign(user) && (
            <Button onClick={goToCreateCampaign} className="flex-1 font-bold uppercase tracking-wide sm:flex-none">
              <Plus className="mr-2 h-4 w-4" />
              Nova campanha
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => setShowJoinDialog(true)}
            className="flex-1 font-bold uppercase tracking-wide sm:flex-none"
          >
            <KeyRound className="mr-2 h-4 w-4" />
            Ingressar
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <ToggleGroup
        type="single"
        value={filter}
        onValueChange={(v) => { if (v) setFilter(v as CampaignFilter) }}
        className="justify-start"
      >
        {([
          ['todas', 'Todas'],
          ['ativas', 'Ativas'],
          ['arquivadas', 'Arquivadas'],
        ] as const).map(([value, label]) => (
          <ToggleGroupItem
            key={value}
            value={value}
            className="px-4 text-xs font-bold uppercase tracking-widest data-[state=on]:bg-accent data-[state=on]:text-accent-foreground"
          >
            {label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="animate-pulse overflow-hidden rounded-xl">
              <div className="aspect-video bg-section" />
              <CardHeader className="space-y-2">
                <div className="h-4 w-3/4 rounded bg-section" />
                <div className="h-3 w-1/2 rounded bg-section" />
              </CardHeader>
            </Card>
          ))}
        </div>
      ) : filteredTables.length === 0 ? (
        <Card className="rounded-xl border-dashed p-8 text-center sm:p-12">
          <BookOpen className="mx-auto mb-4 h-14 w-14 text-muted-foreground opacity-30" />
          <h3 className="mb-1 text-xl font-black uppercase italic tracking-tighter">
            {tables.length === 0 ? 'Nenhuma campanha encontrada' : 'Nada por aqui'}
          </h3>
          <p className="mx-auto mb-6 max-w-sm font-medium text-muted-foreground">
            {tables.length > 0
              ? 'Nenhuma campanha neste filtro.'
              : canCreateCampaign(user)
              ? 'Crie sua primeira campanha educacional para começar a aventura!'
              : 'Peça o código de acesso à sua turma para ingressar em uma campanha.'
            }
          </p>
          {tables.length === 0 && canCreateCampaign(user) && (
            <Button onClick={goToCreateCampaign} className="font-bold uppercase tracking-wide">
              <Plus className="mr-2 h-4 w-4" />
              Criar primeira campanha
            </Button>
          )}
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
          {filteredTables.map((table) => {
            const isCreator = canManageCampaign(user, table)
            const cover = getTableImage(table)

            return (
              <Card
                key={table.id}
                role="button"
                tabIndex={0}
                onClick={() => handleTableNavigation(table.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleTableNavigation(table.id)
                  }
                }}
                className={cn(
                  'group cursor-pointer gap-0 overflow-hidden rounded-xl p-0 transition-all duration-300 hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                  !table.is_active && 'opacity-[.62]'
                )}
              >
                {/* Capa: imagem real ou painel cinema-dark com glow */}
                <div className="relative aspect-video overflow-hidden bg-hero">
                  {cover ? (
                    <>
                      <img src={cover} alt="" className="absolute inset-0 h-full w-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent transition-colors group-hover:from-black/60" />
                    </>
                  ) : (
                    <>
                      <div className="hero-glow" aria-hidden="true" />
                      <div className="absolute inset-0 bg-gradient-to-t from-hero/90 via-transparent to-transparent" />
                    </>
                  )}

                  <div className="absolute left-3 top-3 sm:left-4 sm:top-4">
                    <Badge className="rounded-sm border-none bg-white/90 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-black">
                      {table.subject}
                    </Badge>
                  </div>

                  <div className="absolute right-3 top-3 sm:right-4 sm:top-4">
                    <DropdownMenu modal={false}>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => e.stopPropagation()}
                          className="flex h-9 w-9 items-center justify-center rounded-full border-white/20 bg-black/50 p-0 text-white hover:bg-black hover:text-white"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenuItem
                          className="cursor-pointer text-xs font-black uppercase tracking-widest"
                          onClick={() => handleTableNavigation(table.id)}
                        >
                          <Eye className="mr-2 h-4 w-4" />
                          Ver detalhes
                        </DropdownMenuItem>

                        {isCreator && (
                          <>
                            <DropdownMenuItem
                              className="cursor-pointer text-xs font-black uppercase tracking-widest"
                              onClick={() => {
                                setSelectedTable(table)
                                setShowEditDialog(true)
                              }}
                            >
                              <Pencil className="mr-2 h-4 w-4" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="cursor-pointer text-xs font-black uppercase tracking-widest"
                              onClick={() => handleDuplicateTable(table.id)}
                            >
                              <Copy className="mr-2 h-4 w-4" />
                              Duplicar
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="cursor-pointer text-xs font-black uppercase tracking-widest text-error focus:bg-error-bg focus:text-error"
                              onClick={() => {
                                setSelectedTable(table)
                                setShowDeleteDialog(true)
                              }}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Excluir
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  <div className="absolute bottom-3 left-3 right-3 sm:bottom-4 sm:left-4 sm:right-4">
                    <h3 className="text-lg font-black uppercase italic leading-tight tracking-[-0.035em] text-white drop-shadow sm:text-xl">
                      {table.name}
                    </h3>
                  </div>
                </div>

                {/* Footer claro: contadores + status */}
                <div className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-4 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                    <span className="flex items-center gap-1.5" title="Membros">
                      <Users className="h-3.5 w-3.5" /> {table.member_count}
                    </span>
                    <span className="flex items-center gap-1.5" title="Personagens">
                      <Heart className="h-3.5 w-3.5 fill-current" /> {table.character_count}
                    </span>
                  </div>
                  <Badge className={cn(
                    'rounded-full border-none px-2.5 py-0.5 text-[10px] font-black uppercase tracking-widest',
                    table.is_active ? 'bg-success-bg text-success' : 'bg-section text-muted-foreground'
                  )}>
                    {table.is_active ? 'Ativa' : 'Arquivada'}
                  </Badge>
                </div>
              </Card>
            )
          })}

          {/* Tile de criar nova campanha */}
          {canCreateCampaign(user) && filter !== 'arquivadas' && (
            <button
              onClick={goToCreateCampaign}
              className="flex min-h-[200px] flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-border bg-section/50 p-6 text-muted-foreground transition-all hover:border-primary/40 hover:bg-accent/50 hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-dashed border-current">
                <Plus className="h-6 w-6" />
              </span>
              <span className="text-xs font-black uppercase tracking-widest">Criar nova campanha</span>
            </button>
          )}
        </div>
      )}

      {/* Estatísticas do usuário — compactas, no rodapé (o foco é entrar nas
          campanhas; os números ficam como apoio, sem empurrar as mesas) */}
      {tables.length > 0 && (
        <div className="grid grid-cols-3 gap-3 pt-2 sm:gap-4">
          {[
            { title: 'Campanhas', count: tables.length, icon: BookOpen },
            { title: 'Membros', count: tables.reduce((sum, table) => sum + (table.member_count || 0), 0), icon: Users },
            { title: 'Personagens', count: tables.reduce((sum, table) => sum + (table.character_count || 0), 0), icon: Heart }
          ].map((stat, i) => (
            <Card key={i} className="rounded-lg shadow-sm">
              <CardContent className="flex flex-col items-center gap-1 p-3 text-center">
                <stat.icon className="h-4 w-4 text-primary" />
                <div className="text-lg font-black italic leading-none tracking-tighter sm:text-2xl">{stat.count}</div>
                <p className="text-[9px] font-black uppercase leading-tight tracking-wide text-muted-foreground sm:text-[10px]">{stat.title}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Diálogos */}
      <JoinCampaignDialog
        open={showJoinDialog}
        onOpenChange={setShowJoinDialog}
      />

      {selectedTable && (
        <>
          <EditTableDialog
            open={showEditDialog}
            onOpenChange={setShowEditDialog}
            table={selectedTable}
            onUpdateTable={handleEditTable}
          />

          <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
                <AlertDialogDescription>
                  Tem certeza de que deseja excluir a campanha "{selectedTable.name}"?
                  Esta ação não pode ser desfeita e todos os dados serão perdidos.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeleteTable}
                  disabled={isLoading}
                  className="bg-destructive hover:bg-destructive/90"
                >
                  {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Excluir
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </div>
  )
}
